import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import _ from 'lodash';
import moment from 'moment';
import commaNumber from 'comma-number';
import './NotificationsRecipients.scss';

import { searchNotificationRecipients, getNotificationRecipient, getNotificationRecipientReceipts } from '../../api/notifications';
import EntityAvatar from './elements/EntityAvatar';
import RangeSelect, { RANGE_LABELS } from './elements/RangeSelect';
import LabeledSelect from './elements/LabeledSelect';
import RecipientSettingsPopover from './elements/RecipientSettingsPopover';
import { RecipientCheckBar, RecipientCheckResult } from './elements/RecipientCheck';
import InfoTip from './elements/InfoTip';
import HeaderToolbar from './elements/HeaderToolbar';
import { humanizeName } from '../../helpers/notification_helpers';

const RECIPIENT_RANGES = ['24h', '7d', '30d', '90d', 'all'];
const ALL = 'all';
const NO_FILTERS = { channel: ALL, outcome: ALL };
const CHANNEL_OPTIONS = [
  { value: ALL, label: 'All' },
  { value: 'push', label: 'push' },
  { value: 'email', label: 'email' },
  { value: 'center', label: 'center' }
];
const OUTCOME_OPTIONS = [
  { value: ALL, label: 'All' },
  { value: 'sent', label: 'sent' },
  { value: 'held', label: 'held' },
  { value: 'shadow', label: 'shadow' },
  { value: 'failed', label: 'failed' }
];
const toQuery = filters => _.omitBy(filters, v => v === ALL);

// One person: who they are, every switch, and the receipts the ledger holds for them.
// The page can hand back the last view (recipient, range, filters) so a switch to another sub-tab and
// back lands where the admin left off; the reads themselves are cheap and run again.
const NotificationsRecipients = ({ initialUser, initialView = null, onViewChange = null, onSelectTemplate, templatesByName, toolbarSlot = null }) => {
  const [recipient, setRecipient] = useState(initialUser || initialView?.recipient || null);
  const [range, setRange] = useState(initialView?.range || '7d');
  const [filters, setFilters] = useState(initialView?.filters || NO_FILTERS);

  useEffect(() => {
    if (onViewChange) onViewChange({ recipient, range, filters });
  }, [onViewChange, recipient, range, filters]);
  const [detail, setDetail] = useState(null);
  const [receipts, setReceipts] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [checkResult, setCheckResult] = useState(null);

  const requestVersion = useRef(0);

  useEffect(() => {
    const version = ++requestVersion.current;
    if (!recipient) return;
    const isCurrent = () => version === requestVersion.current;
    setDetail(previous => (previous?.user.id === recipient.id ? previous : null));
    setReceipts([]);
    setNextCursor(null);
    setIsLoadingMore(false);
    setIsLoading(true);
    setSettingsOpen(false);
    setCheckResult(null);
    Promise.all([
      getNotificationRecipient(recipient.id, range, toQuery(filters)),
      getNotificationRecipientReceipts(recipient.id, range, toQuery(filters))
    ])
      .then(([detailResp, receiptsResp]) => {
        if (!isCurrent()) return;
        setDetail(detailResp);
        setReceipts(receiptsResp.receipts);
        setNextCursor(receiptsResp.nextCursor);
      })
      .catch(() => isCurrent() && window.ALERT?.error?.('Failed to load this recipient.'))
      .finally(() => isCurrent() && setIsLoading(false));
    return () => {
      requestVersion.current++;
    };
  }, [recipient, range, filters]);

  const loadMore = () => {
    const version = requestVersion.current;
    const isCurrent = () => version === requestVersion.current;
    setIsLoadingMore(true);
    getNotificationRecipientReceipts(recipient.id, range, toQuery(filters), nextCursor)
      .then(resp => {
        if (!isCurrent()) return;
        setReceipts(existing => [...existing, ...resp.receipts]);
        setNextCursor(resp.nextCursor);
      })
      .catch(() => isCurrent() && window.ALERT?.error?.('Failed to load more receipts.'))
      .finally(() => isCurrent() && setIsLoadingMore(false));
  };

  const openTemplate = receipt => {
    const template = templatesByName[receipt.type];
    if (template && onSelectTemplate) onSelectTemplate(template, detail?.user || recipient);
  };

  const hasDetail = !!recipient && detail?.user.id === recipient.id;

  return (
    <div className='notifications-recipients-outer'>
      <HeaderToolbar slot={toolbarSlot} className='notifications-recipients-outer'>
        <div className='recipients-top'>
          <RecipientSearch onSelect={setRecipient} initialUser={recipient} />
          {hasDetail && (
            <RecipientCheckBar key={detail.user.id} user={detail.user} templatesByName={templatesByName} onResult={setCheckResult} />
          )}
        </div>
        {hasDetail && (
          <div className='recipients-controls'>
            <RangeSelect value={range} onChange={setRange} options={RECIPIENT_RANGES} />
            <LabeledSelect
              label='Channel'
              value={filters.channel}
              onChange={channel => setFilters({ ...filters, channel })}
              options={CHANNEL_OPTIONS}
            />
            <LabeledSelect
              label='Outcome'
              value={filters.outcome}
              onChange={outcome => setFilters({ ...filters, outcome })}
              options={OUTCOME_OPTIONS}
            />
          </div>
        )}
      </HeaderToolbar>

      {!recipient && <div className='recipients-empty'>Search for a creator, shopper or brand user by name, username, email or id.</div>}
      {recipient && !detail && <div className='recipients-empty'>Loading {recipient.name || recipient.username}…</div>}

      {hasDetail && (
        <>
          <RecipientCard
            detail={detail}
            settingsOpen={settingsOpen}
            onToggleSettings={() => setSettingsOpen(open => !open)}
            onCloseSettings={() => setSettingsOpen(false)}
          />
          <StatsRow stats={detail.stats} isLoading={isLoading} />
          {detail.inFlight.count > 0 && <InFlightNotice inFlight={detail.inFlight} />}
          {checkResult && <RecipientCheckResult result={checkResult} onDismiss={() => setCheckResult(null)} />}
          <ReceiptLog receipts={receipts} onOpenTemplate={onSelectTemplate ? openTemplate : undefined} isLoading={isLoading} range={range} />
          {nextCursor && (
            <div className={cn('load-more', { disabled: isLoadingMore })} onClick={isLoadingMore ? undefined : loadMore}>
              {isLoadingMore ? 'Loading…' : 'Load 50 more'}
            </div>
          )}
        </>
      )}
    </div>
  );
};

NotificationsRecipients.propTypes = {
  toolbarSlot: PropTypes.instanceOf(Element),
  initialUser: PropTypes.object,
  initialView: PropTypes.shape({ recipient: PropTypes.object, range: PropTypes.string, filters: PropTypes.object }),
  onViewChange: PropTypes.func,
  onSelectTemplate: PropTypes.func,
  templatesByName: PropTypes.object.isRequired
};

const searchLabel = user => `${user.username || user.name} · ${user.id}`;

const RecipientSearch = ({ onSelect, initialUser = null }) => {
  const [query, setQuery] = useState(initialUser ? searchLabel(initialUser) : '');
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);

  const search = useMemo(
    () =>
      _.debounce(q => {
        if (q.trim().length < 2) return setResults([]);
        searchNotificationRecipients(q)
          .then(resp => setResults(resp.users))
          .catch(() => setResults([]));
      }, 250),
    []
  );

  const onChange = e => {
    setQuery(e.target.value);
    setIsOpen(true);
    search(e.target.value);
  };

  const choose = user => {
    onSelect(user);
    setQuery(searchLabel(user));
    setIsOpen(false);
  };

  return (
    <div className='recipient-search'>
      <input
        type='text'
        placeholder='Find a recipient by name, username, email or id'
        value={query}
        onChange={onChange}
        onFocus={() => setIsOpen(true)}
        onBlur={() => setTimeout(() => setIsOpen(false), 150)}
      />
      {isOpen && results.length > 0 && (
        <div className='search-results'>
          {results.map(user => (
            <div key={user.id} className='search-result' onMouseDown={() => choose(user)}>
              <EntityAvatar className='result-avatar' image={user.image} name={user.name || user.username} />
              <span className='result-name'>{user.name || user.username}</span>
              <span className='result-sub'>{[user.username && `@${user.username}`, user.email, user.id].filter(Boolean).join(' · ')}</span>
              <AccountTypeBadge user={user} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

RecipientSearch.propTypes = { onSelect: PropTypes.func.isRequired, initialUser: PropTypes.object };

const AccountTypeBadge = ({ user }) => <span className='account-type'>{user.accountType === 'brand' ? 'brand user' : user.accountType}</span>;
AccountTypeBadge.propTypes = { user: PropTypes.object.isRequired };

const RecipientCard = ({ detail, settingsOpen, onToggleSettings, onCloseSettings }) => {
  const { user, settings } = detail;
  // Style pickers (options with choices) are not switches; the count and the off-chips cover switches only.
  const options = _.flatMap(settings.groups, group => group.options).filter(option => !option.choices);
  const isOn = option => settings.values && settings.values[option.setting] !== 'none' && settings.values[option.setting] != null;
  const onCount = options.filter(isOn).length;
  const offOptions = settings.values ? options.filter(option => !isOn(option)) : [];
  const meta = [user.id, user.tierLabel, `${commaNumber(user.accountAgeDays)} days old`].filter(Boolean).join(' · ');

  return (
    <div className='recipient-card'>
      <EntityAvatar className='card-avatar' image={user.image} name={user.name || user.username} />
      <div className='card-identity'>
        <div className='card-name-row'>
          <span className='card-name'>{user.name || user.username}</span>
          <AccountTypeBadge user={user} />
        </div>
        <div className='card-meta'>{meta}</div>
        <div className='card-chips'>
          {settings.values ? (
            <span className='chip summary'>
              {onCount} of {options.length} switches on
            </span>
          ) : (
            <span className='chip off'>no settings row</span>
          )}
          {offOptions.map(option => (
            <code key={option.setting} className='chip off' title={`${option.display}: off for push and email`}>
              {option.setting}
            </code>
          ))}
          <span className='settings-anchor'>
            <span className={cn('chip toggle', { open: settingsOpen })} onClick={onToggleSettings}>
              All settings {settingsOpen ? '⌃' : '⌄'}
            </span>
            {settingsOpen && <RecipientSettingsPopover groups={settings.groups} values={settings.values} onClose={onCloseSettings} />}
          </span>
        </div>
      </div>
    </div>
  );
};

RecipientCard.propTypes = {
  detail: PropTypes.object.isRequired,
  settingsOpen: PropTypes.bool.isRequired,
  onToggleSettings: PropTypes.func.isRequired,
  onCloseSettings: PropTypes.func.isRequired
};

const StatsRow = ({ stats, isLoading }) => {
  const held = _.sum(Object.values(stats.held));
  const cells = [
    { label: 'Sent', value: stats.sent },
    { label: 'Held', value: held },
    { label: 'Shadow', value: stats.shadow },
    { label: 'Failed', value: stats.failed }
  ];
  return (
    <div className={cn('recipient-stats', { loading: isLoading })}>
      {cells.map(cell => (
        <div key={cell.label} className='stat'>
          <span className='stat-value'>{commaNumber(cell.value)}</span>
          <span className='stat-label'>{cell.label}</span>
        </div>
      ))}
      <span className='stats-note'>Same range and filters as the log below.</span>
    </div>
  );
};

StatsRow.propTypes = { stats: PropTypes.object.isRequired, isLoading: PropTypes.bool.isRequired };

// Click moments the worker has claimed but not yet decided; it sweeps every 15 minutes.
const InFlightNotice = ({ inFlight }) => {
  const described = inFlight.moments.map(m => `${humanizeName(m.type).toLowerCase()}, ${moment(m.createdAt).format('HH:mm')}`).join('; ');
  return (
    <div className='in-flight-notice'>
      <span className='info-glyph'>i</span>
      {inFlight.count} click {inFlight.count === 1 ? 'moment' : 'moments'} awaiting a decision ({described}). The worker decides on its next sweep;
      sweeps run every 15 minutes.
    </div>
  );
};

InFlightNotice.propTypes = { inFlight: PropTypes.object.isRequired };

const dayLabel = date => {
  const day = moment(date).startOf('day');
  const today = moment().startOf('day');
  const prefix = day.isSame(today) ? 'Today · ' : day.isSame(today.clone().subtract(1, 'day')) ? 'Yesterday · ' : '';
  return `${prefix}${day.format('ddd MMM D')}`;
};

const ReceiptLog = ({ receipts, onOpenTemplate, isLoading, range }) => {
  const days = useMemo(() => {
    const grouped = _.groupBy(receipts, r => moment(r.createdAt).format('YYYY-MM-DD'));
    return Object.keys(grouped)
      .sort()
      .reverse()
      .map(key => ({ key, label: dayLabel(grouped[key][0].createdAt), receipts: grouped[key] }));
  }, [receipts]);

  return (
    <div className='receipt-log'>
      <div className='log-row head'>
        <span>When</span>
        <span>
          Notification
          <InfoTip message='The notification type, by its registry short code. Click a name to open the template with this recipient loaded.' />
        </span>
        <span>Channel</span>
        <span>
          Outcome
          <InfoTip message='sent: handed to the provider. held: a rule stopped it. shadow: would have sent, but the notification is in shadow mode. failed: the provider rejected it.' />
        </span>
        <span>
          Why
          <InfoTip message='The hold or failure reason exactly as recorded when the decision was made, with what it means in plain words. Never worked out again after the fact.' />
        </span>
      </div>
      {days.map(day => (
        <React.Fragment key={day.key}>
          <div className='log-day'>{day.label}</div>
          {day.receipts.map(receipt => (
            <div key={receipt.id} className='log-row'>
              <span className='when'>{moment(receipt.createdAt).format('HH:mm')}</span>
              <span className='notification' onClick={onOpenTemplate ? () => onOpenTemplate(receipt) : undefined}>
                <span className='notification-name'>{humanizeName(receipt.type)}</span>
                <span className='notification-context'>
                  <code>{receipt.type}</code>
                  {receipt.variant && <span className='variant'>variant {receipt.variant}</span>}
                </span>
              </span>
              <span>
                <span className={cn('channel-badge', receipt.channel)}>{receipt.channel}</span>
              </span>
              <span>
                <span className={cn('outcome-badge', receipt.outcome)}>{receipt.outcome}</span>
              </span>
              <span className='why'>
                {receipt.holdReason && <code className='hold-reason'>{receipt.holdReason}</code>}
                {receipt.failureReason && <code className='failure-reason'>{receipt.failureReason}</code>}
                {receipt.why && <span className='gloss'>{receipt.why}</span>}
              </span>
            </div>
          ))}
        </React.Fragment>
      ))}
      {!isLoading && receipts.length === 0 && <div className='log-empty'>No receipts match in the {RANGE_LABELS[range].toLowerCase()}.</div>}
    </div>
  );
};

ReceiptLog.propTypes = {
  receipts: PropTypes.array.isRequired,
  onOpenTemplate: PropTypes.func,
  isLoading: PropTypes.bool.isRequired,
  range: PropTypes.string.isRequired
};

export default NotificationsRecipients;
